/**
 * Trial Banner Component
 *
 * Full-width banner shown below the mobile header.
 * Displays trial status and upgrade CTA without cramping the header.
 *
 * @see docs/TRIAL_SPACES_SPEC.md
 */

import type { TrialStatus } from '@/types/session'

interface TrialBannerProps {
  trialStatus: TrialStatus | null
  isPaidAccount?: boolean
  onUpgradeClick?: () => void
}

export function TrialBanner({ trialStatus, isPaidAccount, onUpgradeClick }: TrialBannerProps) {
  if (!trialStatus) return null
  if (isPaidAccount) return null

  // Don't show banner if expired (they'll be on PlanGate)
  if (trialStatus.isExpired || (!trialStatus.isTrialActive && !trialStatus.isGracePeriod)) {
    return null
  }

  // PDR 011 §6 trial lifecycle. The backend's grace window (day TRIAL_DAYS →
  // grace end) IS the LOCKED window — the workspace is write-locked, reachable
  // only to upgrade or export. We message three checkpoints off the session
  // clock: pre-lock emphasis (active, ≤3 days to lock), locked, and the
  // imminent-deletion urgency (locked, ≤3 days = 72h to deletion).
  const isLocked = trialStatus.isGracePeriod
  const hoursLeft = trialStatus.hoursRemaining ?? 0
  const isPreDelete = isLocked && hoursLeft > 0 && hoursLeft <= 72
  const isEndingSoon = !isLocked && trialStatus.daysRemaining <= 3

  let text: string
  if (isPreDelete) {
    text = `Trial ended — workspace deletes in ${hoursLeft}h. Upgrade or export now.`
  } else if (isLocked) {
    text = 'Trial ended — upgrade to restore access, or export your data.'
  } else {
    const days = `${trialStatus.daysRemaining} day${trialStatus.daysRemaining !== 1 ? 's' : ''}`
    text = isEndingSoon ? `Trial ends in ${days} — upgrade to keep your workspace.` : `Free plan: ${days} left`
  }

  return (
    <div
      className={`trial-banner ${isLocked ? 'trial-banner--grace' : 'trial-banner--active'}`}
    >
      <span className="trial-banner__text">{text}</span>
      {onUpgradeClick && (
        <button className="trial-banner__btn" onClick={onUpgradeClick}>
          Upgrade
        </button>
      )}
    </div>
  )
}
